import cx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';

import { DeviceContext } from '@/components/DeviceProvider/DeviceProvider';
import { SearchIcon } from '@/components/icons';
import { SMALL_BREAKPOINT } from '@/components/modular-grid/constants';
import { useWindowSize } from '@/hooks/useWindowSize';
import { isIFrameMode } from '@/logic/main/utils';
import { isHybrid888 } from '@/utils/isHybrid888';

import styles from './SearchInput.module.scss';

interface SearchInputProps {
  state: 'active' | 'inactive';
  searchPhase?: 'processing' | 'finished';
  placeholderText_New: {
    default: string;
    active: string;
  };
  placeholders?: string[];
  clearText: string;
  searchLink?: string;
  onChange?: (args: { value: string; sourceType: string }) => void;
  onClearButtonClick?: (args: { value: string }) => void;
  onSearchButtonClick?: (args: { value: string; sourceType: string }) => void;
  onSearchWrapperClick?: (args: { path: string }) => void;
  onSoftKeyboardVisibilityChange?: (args: {
    behavior: 'open' | 'close';
  }) => void;
  debounceTimeout?: number;
  searchPhrase?: string;
  inputConfig?: {
    isIOS: boolean;
    isRenderedInIFrame: boolean;
  };
  suggestedPhrase?: string;
}

const SearchInput: React.FC<SearchInputProps> = ({
  state,
  placeholderText_New,
  placeholders = [],
  clearText,
  searchLink,
  onChange,
  onClearButtonClick,
  onSearchButtonClick,
  onSearchWrapperClick,
  onSoftKeyboardVisibilityChange,
  debounceTimeout = 350,
  inputConfig = {
    isIOS: false,
    isRenderedInIFrame: false,
  },
  suggestedPhrase,
}) => {
  let phase = useRef<number>(0);
  const timerID = useRef<NodeJS.Timeout | null>(null);
  const timeoutID = useRef<NodeJS.Timeout | null>(null);
  const windowWidth = useWindowSize().width;
  const [phrase, setPhrase] = useState<string>('');
  const [shouldRefetch, setShouldRefetch] = useState(false);
  const [searchInputFocused, setSearchInputFocused] = useState<boolean>(
    !onSearchWrapperClick
  );
  const searchPlaceholderText =
    placeholderText_New &&
    (searchInputFocused
      ? placeholderText_New.active
      : placeholderText_New.default);

  const [statePlaceholderText, setStatePlaceholderText] = useState<string>(
    searchPlaceholderText
  );
  const isIFrameModeEnabled = isIFrameMode();
  const { deviceType } = React.useContext(DeviceContext);
  const isHybrid888App = isHybrid888();

  useEffect(() => {
    if (suggestedPhrase) {
      setShouldRefetch(false);
      setPhrase(suggestedPhrase);
    }
  }, [suggestedPhrase]);

  useEffect(() => {
    if (shouldRefetch && phrase && phrase.length >= 3) {
      const handler = setTimeout(() => {
        onChange && onChange({ value: phrase, sourceType: 'Search Input' });
      }, debounceTimeout);

      return () => {
        clearTimeout(handler);
      };
    }
    return () => {};
  }, [phrase, debounceTimeout, onChange, shouldRefetch]);

  useEffect(() => {
    if (
      inputConfig.isIOS &&
      inputConfig.isRenderedInIFrame &&
      windowWidth <= SMALL_BREAKPOINT.max
    ) {
      onSoftKeyboardVisibilityChange &&
        onSoftKeyboardVisibilityChange({
          behavior: searchInputFocused ? 'open' : 'close',
        });
    }
  }, [
    searchInputFocused,
    inputConfig,
    onSoftKeyboardVisibilityChange,
    windowWidth,
  ]);

  useEffect(() => {
    const animatePhrase = (txt: string, interval: number | null) => {
      let currentPos = 1;
      const txtLength = txt.length;

      if (timerID.current) clearInterval(timerID.current);
      if (interval && txtLength) {
        timerID.current = setInterval(() => {
          if (txtLength === currentPos) {
            phase.current++;
            startNextAnimationPhase(1800);
          }
          changePlaceholderText(txt.slice(0, currentPos));
          currentPos++;
        }, interval);
      }
    };

    const changePlaceholderText = (txt: string) => setStatePlaceholderText(txt);

    const startNextAnimationPhase = (timeout: number) => {
      if (timeoutID.current) {
        clearTimer();
      }
      if (phase.current > placeholders.length - 1) {
        phase.current = 0;
      }

      timeoutID.current = setTimeout(() => {
        animatePhrase(placeholders[phase.current], 25);
      }, timeout);
    };

    const clearTimer = () => {
      if (timeoutID.current) {
        clearTimeout(timeoutID.current);
      }
    };

    !!(
      deviceType === 'desktop' &&
      placeholders.length &&
      onSearchWrapperClick
    ) && startNextAnimationPhase(500);

    return () => {
      clearTimer();
    };
  }, [deviceType, placeholders, onSearchWrapperClick]);

  const onFocus = () => setSearchInputFocused(true);
  const onBlur = () => setSearchInputFocused(false);

  const createClickHandler = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    onSearchWrapperClick && onSearchWrapperClick({ path: searchLink || '' });
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShouldRefetch(true);
    setPhrase(event.target.value);
  };

  const handleSearchClick = () => {
    onSearchButtonClick!({ value: phrase, sourceType: 'Search Input' });
    setPhrase('');
  };

  const handleClearClick = () => {
    onClearButtonClick && onClearButtonClick({ value: '' });
    setPhrase('');
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSearchButtonClick!({ value: phrase, sourceType: 'Search Input' });
      setPhrase('');
    }
  };

  const handleInputClick = () => {
    setStatePlaceholderText('');
    setPhrase('');
  };

  if (onSearchWrapperClick) {
    return (
      <a
        className={cx(
          styles.search,
          state === 'active' ? styles.searchActive : styles.searchInactive,
          deviceType === 'desktop' && !isIFrameModeEnabled && styles.desktop,
          isHybrid888App && styles.searchHybrid888
        )}
        data-testid="search-wrapper"
        onClick={createClickHandler}
        href={searchLink}
        rel={'nofollow'}
      >
        <input
          id="search-input"
          className={styles.input}
          autoFocus={false}
          readOnly={true}
          placeholder={statePlaceholderText}
          onFocus={onFocus}
          onBlur={onBlur}
          data-testid="search-input"
        />
        <div className={styles.iconWrapper} data-testid="search-icon">
          <SearchIcon
            className={styles.iconInactive}
            deviceType={deviceType}
            isHybrid888App={isHybrid888App}
          />
        </div>
      </a>
    );
  }

  const clearVisible = phrase !== '' || state === 'active';

  return (
    <div
      data-testid="search-wrapper"
      className={cx(
        styles.search,
        state === 'active' ? styles.searchActive : styles.searchInactive,
        deviceType === 'desktop' && !isIFrameModeEnabled && styles.desktop,
        isHybrid888App && styles.searchHybrid888
      )}
    >
      <input
        id="search-input"
        className={styles.input}
        autoFocus={true}
        readOnly={false}
        placeholder={searchPlaceholderText === '' ? '' : searchPlaceholderText}
        onChange={handleChange}
        onClick={handleInputClick}
        onKeyDown={handleKeyDown}
        onFocus={onFocus}
        onBlur={onBlur}
        value={phrase}
        data-testid="search-input"
        maxLength={50}
      />
      {clearVisible && (
        <span
          className={styles.clear}
          data-testid="search-clear"
          onClick={handleClearClick}
        >
          {clearText}
        </span>
      )}
      <div
        data-testid="search-icon"
        className={styles.iconWrapper}
        onClick={handleSearchClick}
      >
        <SearchIcon deviceType={deviceType} isHybrid888App={isHybrid888App} />
      </div>
    </div>
  );
};

export default SearchInput;
